import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { Star } from "lucide-react";
import { useTranslation } from 'react-i18next';

interface PulseItemDialogProps {
  open: boolean;
  type: 'start' | 'end';
  itemId: string;
  onComplete: (data: { itemId: string; construct: string; rating: number }[] | null) => void;
}

export function PulseItemDialog({ open, type, itemId, onComplete }: PulseItemDialogProps) {
  const { t } = useTranslation();

  const PULSE_ITEMS: Record<string, { id: string; construct: string; text: string }[]> = {
    S1: [
      { id: 'S1a', construct: t('pulse.S1a.construct'), text: t('pulse.S1a.question') },
      { id: 'S1b', construct: t('pulse.S1b.construct'), text: t('pulse.S1b.question') },
    ],
    S2: [
      { id: 'S2a', construct: t('pulse.S2a.construct'), text: t('pulse.S2a.question') },
      { id: 'S2b', construct: t('pulse.S2b.construct'), text: t('pulse.S2b.question') },
    ],
    S3: [
      { id: 'S3a', construct: t('pulse.S3a.construct'), text: t('pulse.S3a.question') },
      { id: 'S3b', construct: t('pulse.S3b.construct'), text: t('pulse.S3b.question') },
    ],
    E1: [{ id: 'E1', construct: t('pulse.E1.construct'), text: t('pulse.E1.question') }],
    E2: [{ id: 'E2', construct: t('pulse.E2.construct'), text: t('pulse.E2.question') }],
    E3: [{ id: 'E3', construct: t('pulse.E3.construct'), text: t('pulse.E3.question') }],
  };

  const items = PULSE_ITEMS[itemId as keyof typeof PULSE_ITEMS] || [];
  const [ratings, setRatings] = useState<Record<string, number>>({});

  console.log('PulseItemDialog rendered:', { open, type, itemId, items: items.map(i => i.id) });

  useEffect(() => {
    if (open) {
      setRatings({});
    }
  }, [open, type, itemId]);

  const handleSubmit = () => {
    const allRated = items.every(item => ratings[item.id] !== undefined);

    if (allRated) {
      const results = items.map(item => ({
        itemId: item.id,
        construct: item.construct,
        rating: ratings[item.id]
      }));
      onComplete(results);
    }
  };

  const setRating = (itemId: string, rating: number) => {
    setRatings(prev => ({ ...prev, [itemId]: rating }));
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-[420px] !p-0 !gap-0 border-none shadow-2xl bg-white dark:bg-gray-900 rounded-3xl overflow-hidden [@media(max-height:500px)]:!max-w-[400px] [@media(max-height:500px)]:!rounded-[20px]"
        onPointerDownOutside={(e) => e.preventDefault()}
        showCloseButton={false}
        showTitle={false}
      >
        <AnimatePresence mode="wait">
          {items.length > 0 && (
            <motion.div
              key="question"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="px-6 pb-6 pt-6 space-y-4 [@media(max-height:500px)]:!p-4 [@media(max-height:500px)]:!space-y-3">
                {/* 遍历显示所有题目 - 卡片式布局 */}
                {items.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -2 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm [@media(max-height:500px)]:!p-3"
                  >
                    {/* 题目标题和文本 */}
                    <div className="mb-3 [@media(max-height:500px)]:!mb-2">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 leading-snug">
                        {item.text}
                      </p>
                    </div>

                    {/* Star Rating Display */}
                    <div className="flex justify-start gap-3 [@media(max-height:500px)]:!gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <motion.button
                          key={star}
                          onClick={() => setRating(item.id, star)}
                          whileHover={{ scale: 1.15 }}
                          whileTap={{ scale: 0.9, rotate: 15 }}
                          className="focus:outline-none"
                        >
                          <Star
                            className={`w-8 h-8 transition-all duration-200 [@media(max-height:500px)]:!w-7 [@media(max-height:500px)]:!h-7 ${
                              ratings[item.id] && star <= ratings[item.id]
                                ? 'fill-yellow-400 text-yellow-400 drop-shadow-sm'
                                : 'fill-gray-200 text-gray-200 dark:fill-gray-700 dark:text-gray-700'
                            }`}
                          />
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                ))}

                {/* Submit Button */}
                <Button
                  onClick={handleSubmit}
                  disabled={!items.every(item => ratings[item.id] !== undefined)}
                  className="w-full py-5 text-base font-semibold rounded-xl bg-[var(--practice-dialog-accent)] text-[var(--practice-dialog-accent-fg)] hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed shadow-md transition-all mt-4 [@media(max-height:500px)]:!py-3 [@media(max-height:500px)]:!text-sm [@media(max-height:500px)]:!mt-2"
                >
                  {t('pulse.submit')}
                </Button>

              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
